import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  deleteGalleryItem,
  fetchGalleryCounts,
  fetchGalleryList,
  uploadGalleryItem,
} from '@/features/gallery/api/galleryApi';
import type {
  GalleryListFilter,
  GalleryUploadPayload,
} from '@/features/gallery/types/gallery';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { getErrorMessage } from '@/shared/api/errors';

export function useGalleryListQuery(filter: GalleryListFilter) {
  return useQuery({
    queryKey: queryKeys.gallery.list(filter),
    queryFn: () => fetchGalleryList(filter),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useGalleryCountsQuery() {
  return useQuery({
    queryKey: queryKeys.gallery.counts(),
    queryFn: fetchGalleryCounts,
    refetchOnWindowFocus: false,
  });
}

export function useUploadGalleryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: GalleryUploadPayload) => uploadGalleryItem(payload),
    onSuccess: (res) => {
      toast.success(res?.message || 'Content uploaded');
      void queryClient.invalidateQueries({ queryKey: queryKeys.gallery.root });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Upload failed'));
    },
  });
}

export function useDeleteGalleryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteGalleryItem(id),
    onSuccess: (res) => {
      toast.success(res?.message || 'Content deleted');
      void queryClient.invalidateQueries({ queryKey: queryKeys.gallery.root });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Delete failed'));
    },
  });
}
